import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Layout from '../../../../components/Layout/Layout'
import { toast } from 'react-toastify'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../../context/auth'
import { Modal } from 'antd'
import AdminStoreRoomCheckListTable from './CheckListTable'
import StoreRoomQtyLog from './StoreRoomQtyLog'
import SameRackBoxNoSelect from './SameRackBoxNoSelect'
import BoxAddItem from './AddItem'

// Boxes with this many rows or fewer show no search box at all.
const SEARCH_MIN_ROWS = 10

const AdminStoreRoomCheckList = () => {
  const navigate = useNavigate()
  const { id, rackno, boxno } = useParams()

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const [searchData, setSearchData] = useState('')
  const [focusBarcodeInput, setFocusBarcodeInput] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [visibleLogModal, setVisibleLogModal] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(null)
  const [currentItemDetails, setCurrentItemDetails] = useState({})
  const [editMode, setEditMode] = useState(null)
  const [editField, setEditField] = useState(null)
  const [originalValues, setOriginalValues] = useState({})
  const [filterQuery, setFilterQuery] = useState('')
  const [, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)

  const [isBoxModalVisible, setIsBoxModalVisible] = useState(false)
  const [boxStatus, setBoxStatus] = useState(0)
  const [isAddItemModalVisible, setIsAddItemModalVisible] = useState(false)

  const barcodeInputRef = useRef(null)
  const [auth] = useAuth()
  const userId = auth?.user?._id

  useEffect(() => {
    const shouldFetch = filterQuery.length > 3 || filterQuery === ''
    if (shouldFetch) {
      // Reset the scroll position too. Leaving `page` at its old value made
      // the next infinite-scroll step jump straight to page+1 of the new
      // result set, skipping the pages in between.
      setPage(1)
      fetchData(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rackno, boxno, id, filterQuery])

  useEffect(() => {
    if (page > 1) {
      fetchData(page)
    }
  }, [page])

  const requestIdRef = useRef(0)
  const abortRef = useRef(null)

  const fetchData = async (newPage = 1, showToast = false) => {
    // Only the newest request may write to state. Without this, a slow page-3
    // response could land after a page-1 response and append the wrong rows.
    const requestId = ++requestIdRef.current
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/admin/stock-check-store/get-storeroom-data/${rackno}/${boxno}/${id}`,
        {
          params: {
            userId,
            page: newPage,
            limit: 30,
            search: filterQuery,
          },
          signal: controller.signal,
          timeout: 30000,
        }
      )
      if (requestId !== requestIdRef.current) return
      const { data, total, totalPages } = response.data
      setTotalPages(totalPages)
      setTotalRecords(total ?? 0)
      if (newPage === 1) {
        setItems(data)
      } else {
        setItems((prev) => [...prev, ...data])
      }
      setHasMore(newPage < totalPages)
      if (showToast) {
        toast.success('Data is up to date')
      }
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return
      if (requestId !== requestIdRef.current) return
      console.error('Failed to fetch stock list data:', error)
      // Keep what is already on screen. Blanking the list on a dropped request
      // looked like lost data and invited people to re-scan counted items.
      setHasMore(false)
      toast.error('Could not load data. Check the connection and refresh.')
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    const fetchBoxStatus = async () => {
      try {
        const response = await axios.get(
          `/api/v1/admin/stock-check-store/get-boxes-status/${rackno}/${boxno}/${id}`
        )
        setBoxStatus(response.data.boxStatus)
      } catch (error) {
        // Silent before: a failure left the button reading "Mark Checked"
        // with no hint that the real status was never loaded.
        console.error('Failed to fetch box status', error)
        toast.error('Could not load the checked status for this box.')
      }
    }

    fetchBoxStatus()
  }, [rackno, boxno, id])

  const handleBoxChecked = async () => {
    const confirmed = window.confirm(`Confirm that the box is checked?`)
    if (!confirmed) return

    try {
      await axios.patch(
        `/api/v1/admin/stock-check-store/update-box-status/${id}`,
        {
          rackno: rackno,
          boxno: boxno,
        }
      )

      setBoxStatus(1)
      toast.success('Checked successfully!')
    } catch (error) {
      console.error('Failed to update box status:', error)
      toast.error('Failed to update box status.')
    }
  }

  useEffect(() => {
    if (focusBarcodeInput) {
      barcodeInputRef.current?.focus()
      setFocusBarcodeInput(false)
    }
  }, [focusBarcodeInput])

  const clearInput = () => {
    setSearchData('')
    setFocusBarcodeInput(true)
  }

  const getSearchContent = (value) => {
    setSearchData(value)
  }

  const handleKeyPress = async (event) => {
    if (event.key === 'Enter' && searchData.trim()) {
      await scanResult()
    }
  }

  const scanResult = async () => {
    try {
      const { data } = await axios.post(
        '/api/v1/admin/stock-check-store/scan-storeroom-stock',
        { searchData, id, rackno, boxno, userId }
      )
      if (data.products.length === 0) {
        toast.info('No record found')
      } else {
        updateScannedProduct(data.products[0])
        setLastUpdate(new Date())
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.warning(error.response.data.message)
      } else {
        const errorMessage = error.response?.data?.message
        toast.error(errorMessage)
      }
    } finally {
      clearInput()
    }
  }

  const updateScannedProduct = (scannedProduct) => {
    // Assuming auth context holds the current user info
    const userCode = auth?.user?.code

    setItems((prevItems) => {
      // Find the existing item in the list
      const existingItem = prevItems.find(
        (item) => item._id === scannedProduct._id
      )

      // scannedProduct is the post-increment document from the server, so its
      // checkQty is authoritative. Guessing it locally as "+1" drifted from the
      // DB whenever two people scanned the same item.
      const updatedProduct = {
        ...existingItem,
        ...scannedProduct,
        lastUptByCode: userCode,
        lastUptDate: new Date(),
      }

      // Append the updated product to the top and filter out the old instance
      const updatedItems = [
        updatedProduct,
        ...prevItems.filter((item) => item._id !== scannedProduct._id),
      ]

      return updatedItems
    })
  }

  const typedQtyRef = useRef({})
  const observer = useRef()
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1) // Load next page when the last element is visible
        }
      })
      if (node) observer.current.observe(node)
    },
    [isLoading, hasMore]
  )

  const toggleEdit = (itemId, field, currentValue) => {
    const key = `${itemId}-${field}`
    if (editMode === itemId && editField === field) {
      setEditMode(null)
      setEditField(null)
      setItems((currentList) =>
        currentList.map((item) =>
          item._id === itemId
            ? {
                ...item,
                // ?? not || - a saved original of 0 is a real value and must
                // not fall through to the edited one.
                [field]: originalValues[key] ?? item[field],
              }
            : item
        )
      )
    } else {
      setEditMode(itemId)
      setEditField(field)
      setOriginalValues((prev) => ({
        ...prev,
        // Re-baseline on every open. Keeping the first-ever baseline made the
        // "unchanged" check in saveQty match a stale value and skip the save,
        // leaving the screen and the DB disagreeing.
        [key]: currentValue ?? '',
      }))
    }
  }

  const handleQtyKeyPress = (event, itemId, type) => {
    if (event.key === 'Enter') {
      event.preventDefault() // Prevent default form submission behavior
      saveQty(itemId, type) // Make sure to pass the correct type
    }
  }

  const handleQtyChange = (itemId, value, type) => {
    // Remember the cell was actually typed in, so re-entering the same number
    // still gets saved and logged rather than being skipped as "unchanged".
    typedQtyRef.current[itemId] = true
    setItems((currentList) =>
      currentList.map((item) =>
        item._id === itemId ? { ...item, [type]: value } : item
      )
    )
  }

  const saveQty = async (itemId, type) => {
    const item = items.find((item) => item._id === itemId)
    if (!item) return

    const key = `${itemId}-${type}`
    const previousValue = originalValues[key]
    const raw = String(item.checkQty ?? '').trim()
    const updatedValue = Number(raw)

    const closeEditor = () => {
      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
    }

    // Blank or non-numeric: restore the old value instead of saving 0.
    if (raw === '' || !Number.isFinite(updatedValue) || updatedValue < 0) {
      setItems((list) =>
        list.map((i) => (i._id === itemId ? { ...i, [type]: previousValue } : i))
      )
      closeEditor()
      toast.error('Quantity must be a number of 0 or more.')
      return
    }

    const wasTyped = typedQtyRef.current[itemId]
    delete typedQtyRef.current[itemId]

    // Only skip the save when the editor was opened and closed untouched.
    if (Number(previousValue) === updatedValue && !wasTyped) {
      closeEditor()
      return
    }

    // Show the new value straight away, then confirm it with the server.
    const updatedProduct = {
      ...item,
      checkQty: updatedValue,
      lastUptByCode: auth?.user?.code,
      lastUptDate: new Date(),
    }
    setItems((prevItems) => [
      updatedProduct,
      ...prevItems.filter((i) => i._id !== itemId),
    ])
    // The row just moved to the top of the list, so follow it there.
    window.scrollTo({ top: 0 })
    closeEditor()

    try {
      await axios.patch(
        `/api/v1/admin/stock-check-store/update-storeroom-qty/${itemId}`,
        {
          checkQty: updatedValue,
          userId,
          id,
          rackno,
          boxno,
        }
      )
    } catch (error) {
      // Roll back so the screen never shows a qty the DB does not hold.
      setItems((prevItems) =>
        prevItems.map((i) =>
          i._id === itemId ? { ...i, checkQty: previousValue } : i
        )
      )
      console.error(`Failed to update ${type}:`, error)
      toast.error(
        error.response?.data?.message || `Failed to update ${type}. Not saved.`
      )
    }
  }

  const handleFilterChange = (value) => {
    setFilterQuery(value)
  }

  const handleRemarkChange = (itemId, value) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item._id === itemId ? { ...item, userRemark: value } : item
      )
    )
  }

  const handleRemarkKeyPress = (event, itemId) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveRemark(itemId)
    }
  }

  const saveRemark = async (itemId) => {
    const item = items.find((item) => item._id === itemId)
    if (!item) return

    const original = (originalValues[`${itemId}-userRemark`] || '').trim()
    const current = (item.userRemark || '').trim()
    if (original === current) {
      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
      return
    }

    // Show it straight away, then confirm with the server.
    const updatedItem = {
      ...item,
      userRemark: current,
      lastUptByCode: auth?.user?.code,
      lastUptDate: new Date(),
    }
    setItems((prevItems) => [
      updatedItem,
      ...prevItems.filter((i) => i._id !== itemId),
    ])
    // The row just moved to the top of the list, so follow it there.
    window.scrollTo({ top: 0 })
    setEditMode(null)
    setEditField(null)
    setFocusBarcodeInput(true)

    try {
      await axios.patch(
        `/api/v1/admin/stock-check-store/update-storeroom-remark/${itemId}`,
        { userRemark: current, userId }
      )
    } catch (error) {
      setItems((prevItems) =>
        prevItems.map((i) =>
          i._id === itemId ? { ...i, userRemark: original } : i
        )
      )
      console.error('Failed to update remark:', error)
      toast.error(
        error.response?.data?.message || 'Failed to update remark. Not saved.'
      )
    }
  }

  const openLogModal = (itemId) => {
    const item = items.find((item) => item._id === itemId)
    if (item) {
      setCurrentItemId(itemId)
      setCurrentItemDetails({
        itemCode: item.itemCode,
        description: item.description,
      })
      // Re-stamp so the log refetches on every open. Manual qty saves do not
      // touch lastUpdate, so reopening the same item used to show the list
      // fetched earlier and miss the entry just written.
      setLastUpdate(new Date())
      setVisibleLogModal(true)
    }
  }

  const closeModal = (e) => {
    e.preventDefault()
    setVisibleLogModal(false)
  }

  const showBoxModal = () => {
    setIsBoxModalVisible(true)
  }

  const hideBoxModal = () => {
    setIsBoxModalVisible(false)
  }

  const handleItemAdded = (newItem) => {
    setItems((prevItems) => [newItem, ...prevItems])
  }

  // Search only earns its space once the box holds more than a screenful.
  const showSearch = totalRecords > SEARCH_MIN_ROWS || filterQuery !== ''

  return (
    <Layout title='MW-102 Check List'>
      {/* Normal flow, not position:fixed with a hard-coded 120px height. The
          toolbar wraps on a narrow screen, and a fixed bar could not grow
          without covering the first rows of the table. */}
      <div
        className='shadow-sm border-bottom px-3 px-md-4 pt-3 pb-3 bg-white'
        // sticky, not fixed: it still sits in the flow so it can wrap to
        // another row without covering the table, but stays put on scroll.
        // top clears the fixed navbar. z-index must stay BELOW antd's modal
        // base of 1000, or the bar paints over the log / Add Item dialogs.
        style={{ position: 'sticky', top: '50px', zIndex: 900 }}
      >
        <div className='d-flex align-items-center flex-wrap gap-3 mb-3'>
          {/* A breadcrumb replaces the two identical "Back" buttons - each
              step says where it goes, and the last crumb is the location, so
              the separate centred title is no longer needed. */}
          <nav aria-label='breadcrumb' className='me-auto'>
            <ol className='breadcrumb wms-breadcrumb mb-0'>
              <li className='breadcrumb-item'>
                <button
                  type='button'
                  className='btn btn-link p-0 text-decoration-none'
                  onClick={() => navigate(`/admin/stock-check/details/${id}`)}
                >
                  Stock Check
                </button>
              </li>
              <li className='breadcrumb-item'>
                <button
                  type='button'
                  className='btn btn-link p-0 text-decoration-none'
                  onClick={() => navigate(`/admin/stock-check/rack-box/${id}`)}
                >
                  Racks
                </button>
              </li>
              <li className='breadcrumb-item active' aria-current='page'>
                <span className='wms-crumb-current'>
                  MW-102 · {rackno} {boxno !== 'empty' ? boxno : ''}
                </span>
              </li>
            </ol>
          </nav>

          {/* A box with a handful of rows needs no search. The filter stays
              visible while one is active, otherwise narrowing the results
              would hide the only way to clear it. */}
          {showSearch && (
            <div className='d-flex'>
              <input
                type='text'
                className='form-control'
                style={{ width: '250px', maxWidth: '100%' }}
                placeholder='Search Item Code or Desc'
                onChange={(e) => handleFilterChange(e.target.value)}
                value={filterQuery}
              />
              <button
                className='btn btn-secondary'
                onClick={() => setFilterQuery('')}
              >
                Clear
              </button>
            </div>
          )}

          <button
            type='button'
            className='btn btn-info'
            onClick={() => fetchData(1, true)}
            aria-label='Refresh'
            title='Refresh'
          >
            <i className='bi bi-arrow-clockwise'></i>
          </button>
        </div>

        <div className='d-flex flex-wrap gap-3'>
          <div className='d-flex flex-grow-1' style={{ minWidth: '240px' }}>
            <input
              type='text'
              className='form-control'
              placeholder='Scan / Enter Item Code'
              onChange={(e) => getSearchContent(e.target.value)}
              onKeyDown={handleKeyPress}
              value={searchData}
              style={{ flex: 1 }}
              autoFocus
              ref={barcodeInputRef}
            />
            <button
              type='button'
              className='btn btn-secondary'
              disabled={!searchData}
              onClick={clearInput}
            >
              Clear
            </button>
          </div>

          <div className='d-flex flex-wrap gap-3'>
            <button
              type='button'
              className='btn btn-primary'
              onClick={showBoxModal}
            >
              Other Boxes
            </button>

            <button
              type='button'
              className={`btn ${
                boxStatus === 1 ? 'btn-secondary' : 'btn-success'
              }`}
              onClick={handleBoxChecked}
              disabled={boxStatus === 1}
            >
              {boxStatus === 1 ? 'Checked' : 'Mark Checked'}
            </button>
            <button
              type='button'
              className='btn btn-wms-add'
              onClick={() => setIsAddItemModalVisible(true)}
            >
              Add Item
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .wms-breadcrumb {
          --bs-breadcrumb-divider: '\\203A';
          font-size: 0.95rem;
          align-items: center;
        }
        /* Flex so the divider centres against the crumb text instead of
           sitting on the baseline of its own larger glyph. */
        .wms-breadcrumb .breadcrumb-item {
          display: flex;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
          color: #adb5bd;
          padding: 0 0.6rem;
          font-size: 1.1rem;
        }
        .wms-breadcrumb .btn-link {
          color: #6c757d;
          font-weight: 500;
          vertical-align: baseline;
        }
        .wms-breadcrumb .btn-link:hover,
        .wms-breadcrumb .btn-link:focus-visible {
          color: rgb(47, 68, 131);
        }
        /* The location is the heading of this page, so it carries the weight. */
        .wms-crumb-current {
          font-size: 1.15rem;
          font-weight: 700;
          color: #198754;
        }
      `}</style>

      <div>
        <div className='table-responsive'>
          <AdminStoreRoomCheckListTable
            items={items}
            isLoading={isLoading}
            currentPage={page}
            lastElementRef={lastElementRef}
            editMode={editMode}
            editField={editField}
            handleQtyChange={handleQtyChange}
            saveQty={saveQty}
            handleQtyKeyPress={handleQtyKeyPress}
            toggleEdit={toggleEdit}
            openLogModal={openLogModal}
            handleRemarkChange={handleRemarkChange}
            handleRemarkKeyPress={handleRemarkKeyPress}
            saveRemark={saveRemark}
            onItemDeleted={(deletedId) =>
              setItems((prev) => prev.filter((item) => item._id !== deletedId))
            }
            originalValues={originalValues}
            setOriginalValues={setOriginalValues}
          />
        </div>
      </div>

      <Modal
        closable={false}
        onCancel={() => setVisibleLogModal(false)}
        open={visibleLogModal}
        // Fixed 70% was still unusably narrow on a phone and needlessly wide
        // on a monitor.
        width='90%'
        style={{ maxWidth: 720 }}
        footer={
          <div className='mt-4'>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => setVisibleLogModal(false)}
            >
              Cancel
            </button>
          </div>
        }
      >
        <StoreRoomQtyLog
          closeLogModal={closeModal}
          itemId={currentItemId}
          itemDetails={currentItemDetails}
          lastUpdate={lastUpdate}
        />
      </Modal>

      <SameRackBoxNoSelect
        id={id}
        rackno={rackno}
        boxno={boxno}
        isModalVisible={isBoxModalVisible}
        hideModal={hideBoxModal}
      />

      <BoxAddItem
        id={id}
        rackno={rackno}
        boxno={boxno}
        isModalVisible={isAddItemModalVisible}
        hideModal={() => setIsAddItemModalVisible(false)}
        onItemAdded={handleItemAdded}
      />
    </Layout>
  )
}

export default AdminStoreRoomCheckList
